import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { clearSessionCache } from '@/services/globalAssistantApi';

// Types
export interface UserOrganization {
  id: string;
  name: string;
  slug: string;
  role: 'owner' | 'admin' | 'member';
}

export interface UserTeam {
  id: string;
  name: string;
  organization_id: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
}

// Backwards compatibility alias
export type UserGroup = UserTeam;

export interface User {
  id: string;
  username: string;
  email?: string;
  display_name?: string;
  is_superadmin?: boolean;  // Only present when user is a superadmin (security: not included for regular users)
  organizations: UserOrganization[];
  teams: UserTeam[];
  // Backwards compatibility getter
  groups?: UserTeam[];
}

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  activeOrganization: UserOrganization | null;
  activeTeam: UserTeam | null;
  // Backwards compatibility alias
  activeGroup: UserTeam | null;
  needsOnboarding: boolean;
  error: string | null;
}

export interface AuthContextValue extends AuthState {
  login: (provider: string, inviteCode?: string) => void;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  setActiveOrganization: (orgId: string) => void;
  setActiveTeam: (teamId: string) => void;
  // Backwards compatibility alias
  setActiveGroup: (groupId: string) => void;
  devLogin: () => void;
}

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  activeOrganization: null,
  activeTeam: null,
  activeGroup: null,
  needsOnboarding: false,
  error: null,
};

// Context
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Storage keys
const ACTIVE_ORG_KEY = 'greenfirefly_active_org';
const ACTIVE_TEAM_KEY = 'greenfirefly_active_team';
// Backwards compatibility
const ACTIVE_GROUP_KEY = 'greenfirefly_active_group';

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(initialState);

  // Fetch session on mount
  const fetchSession = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/session', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch session');
      }

      const data = await response.json();

      if (data.authenticated && data.user) {
        // Normalize user data - handle both old 'groups' and new 'teams' format
        const user: User = {
          ...data.user,
          organizations: data.user.organizations || [],
          teams: data.user.teams || data.user.groups || [],
        };
        // Backwards compatibility: alias groups to teams
        user.groups = user.teams;

        // Restore active organization from localStorage or use first org
        const storedOrgId = localStorage.getItem(ACTIVE_ORG_KEY);
        let activeOrganization: UserOrganization | null = null;

        if (user.organizations && user.organizations.length > 0) {
          activeOrganization = user.organizations.find((o: UserOrganization) => o.id === storedOrgId)
            || user.organizations[0];
          // Persist to localStorage so axios interceptors can read it
          if (activeOrganization && activeOrganization.id !== storedOrgId) {
            localStorage.setItem(ACTIVE_ORG_KEY, activeOrganization.id);
          }
        }

        // Restore active team from localStorage or use first team in active org
        const storedTeamId = localStorage.getItem(ACTIVE_TEAM_KEY) || localStorage.getItem(ACTIVE_GROUP_KEY);
        let activeTeam: UserTeam | null = null;

        if (user.teams && user.teams.length > 0) {
          if (activeOrganization) {
            // Prefer teams from active organization
            const orgTeams = user.teams.filter(t => t.organization_id === activeOrganization?.id);
            activeTeam = orgTeams.find((t: UserTeam) => t.id === storedTeamId)
              || orgTeams[0]
              || user.teams.find((t: UserTeam) => t.id === storedTeamId)
              || user.teams[0];
          } else {
            activeTeam = user.teams.find((t: UserTeam) => t.id === storedTeamId)
              || user.teams[0];
          }
          // Persist to localStorage so axios interceptors can read it
          if (activeTeam && activeTeam.id !== storedTeamId) {
            localStorage.setItem(ACTIVE_TEAM_KEY, activeTeam.id);
            localStorage.setItem(ACTIVE_GROUP_KEY, activeTeam.id);
          }
        }

        setState({
          isAuthenticated: true,
          isLoading: false,
          user,
          activeOrganization,
          activeTeam,
          activeGroup: activeTeam, // Backwards compatibility
          needsOnboarding: data.needs_onboarding || false,
          error: null,
        });
      } else {
        setState({
          ...initialState,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
      setState({
        ...initialState,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Authentication error',
      });
    }
  }, []);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Login - redirect to OAuth provider
  const login = useCallback((provider: string, inviteCode?: string) => {
    let url = `/api/auth/login/${provider}`;
    if (inviteCode) {
      url += `?invite_code=${encodeURIComponent(inviteCode)}`;
    }
    window.location.href = url;
  }, []);

  // Dev login - for development mode
  const devLogin = useCallback(() => {
    window.location.href = '/api/auth/dev-login';
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });

      // Clear stored selections
      localStorage.removeItem(ACTIVE_ORG_KEY);
      localStorage.removeItem(ACTIVE_TEAM_KEY);
      localStorage.removeItem(ACTIVE_GROUP_KEY);
      // Clear cached session token
      clearSessionCache();

      // Reset state
      setState({
        ...initialState,
        isLoading: false,
      });

      // Redirect to login
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, []);

  // Refresh session (after group changes, etc.)
  const refreshSession = useCallback(async () => {
    try {
      await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });
      await fetchSession();
    } catch (error) {
      console.error('Failed to refresh session:', error);
    }
  }, [fetchSession]);

  // Set active organization
  const setActiveOrganization = useCallback((orgId: string) => {
    if (!state.user) return;

    const org = state.user.organizations.find(o => o.id === orgId);
    if (org) {
      localStorage.setItem(ACTIVE_ORG_KEY, orgId);
      // Clear cached session token so next API call gets new one with updated org
      clearSessionCache();

      // Also update active team to one from this org
      const orgTeams = state.user.teams.filter(t => t.organization_id === orgId);
      const newActiveTeam = orgTeams.length > 0 ? orgTeams[0] : state.activeTeam;

      if (newActiveTeam) {
        localStorage.setItem(ACTIVE_TEAM_KEY, newActiveTeam.id);
        localStorage.setItem(ACTIVE_GROUP_KEY, newActiveTeam.id);
      }

      setState(prev => ({
        ...prev,
        activeOrganization: org,
        activeTeam: newActiveTeam,
        activeGroup: newActiveTeam,
      }));
    }
  }, [state.user, state.activeTeam]);

  // Set active team
  const setActiveTeam = useCallback((teamId: string) => {
    if (!state.user) return;

    const team = state.user.teams.find(t => t.id === teamId);
    if (team) {
      localStorage.setItem(ACTIVE_TEAM_KEY, teamId);
      localStorage.setItem(ACTIVE_GROUP_KEY, teamId); // Backwards compatibility

      // Also update active organization if team is in a different org
      const org = state.user.organizations.find(o => o.id === team.organization_id);
      if (org && org.id !== state.activeOrganization?.id) {
        localStorage.setItem(ACTIVE_ORG_KEY, org.id);
        // Clear cached session token when org changes
        clearSessionCache();
        setState(prev => ({
          ...prev,
          activeOrganization: org,
          activeTeam: team,
          activeGroup: team,
        }));
      } else {
        setState(prev => ({
          ...prev,
          activeTeam: team,
          activeGroup: team,
        }));
      }
    }
  }, [state.user, state.activeOrganization]);

  // Backwards compatibility alias
  const setActiveGroup = setActiveTeam;

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    refreshSession,
    setActiveOrganization,
    setActiveTeam,
    setActiveGroup,
    devLogin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Export context for advanced use cases
export { AuthContext };
